import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import numeral from 'numeral';
import throttle from 'lodash/throttle';
import classnames from 'classnames';
import Icon from '../icons/Icon';
import { exitFullScreen, requestFullScreen, fullScreenElement } from './utils/browserPrefixedMethods';
import VideoControls from './VideoControls';
import config from '../../config';

class DesktopVideoPlayer extends Component {
  static propTypes = {
    backText: PropTypes.string,
    backTo: PropTypes.string,
    className: PropTypes.string,
    initialTime: PropTypes.number,
    onBackTo: PropTypes.func,
    onProgress: PropTypes.func,
    progressThrottle: PropTypes.number.isRequired,
    sections: PropTypes.array,
    title: PropTypes.string,
    video: PropTypes.object.isRequired,
    autoplay: PropTypes.bool,
    source: PropTypes.string.isRequired,
    poster: PropTypes.string,
    goBack: PropTypes.func.isRequired,
  };

  state = {
    mouseOver: false,
  };

  mounted = false;

  componentDidMount() {
    const { autoplay, video, source, onPlay } = this.props;

    if (video.get('transcode_status') === 'Complete') {
      this.mounted = true;

      const events = window.dashjs.MediaPlayer.events;

      this.player = window.dashjs.MediaPlayer().create();
      this.player.initialize(this.videoElement);

      this.player.setXHRWithCredentialsForType('MPD', config.cloudfront.withCredentials);
      this.player.setXHRWithCredentialsForType('MediaSegment', config.cloudfront.withCredentials);
      this.player.setXHRWithCredentialsForType('InitializationSegment', config.cloudfront.withCredentials);

      this.player.setAutoPlay(!!autoplay);
      this.player.attachSource(source);

      this.player.on(events.PLAYBACK_ENDED, this.onEnd, this);
      this.player.on(events.PLAYBACK_PROGRESS, this.onProgress, this);
      this.player.on(events.PLAYBACK_STARTED, onPlay, this);

      Object.keys(events).forEach(type => {
        this.player.on(events[type], this.rerender, this);
      });
    }
  }

  componentWillUnmount() {
    const { onPlay } = this.props;
    if (this.mounted) {
      const events = window.dashjs.MediaPlayer.events;

      this.mounted = false;

      clearTimeout(this.mouseTimer);

      this.onProgress.cancel();

      this.videoContainer.removeEventListener('keydown', this.onKeyDown);

      this.player.off(events.PLAYBACK_ENDED, this.onEnd, this);
      this.player.off(events.PLAYBACK_PROGRESS, this.onProgress, this);
      this.player.off(events.PLAYBACK_STARTED, onPlay, this);

      this.player.reset();
    }
  }

  onEnd = () => {
    if (this.props.onEnd) {
      this.props.onEnd();
    }
  };

  onMouseMove = throttle(e => {
    clearTimeout(this.mouseTimer);
    if (this.mounted) {
      this.setState({ mouseOver: true }, () => {
        this.mouseTimer = setTimeout(() => {
          this.setState({ mouseOver: false });
        }, 3000);
      });
    }
  }, 300);

  onKeyDown = e => {
    if (this.player.isReady()) {
      if (e.keyCode === 32 || e.keyCode === 75) {
        this.togglePlayPause();
      } else if (e.keyCode === 70) {
        this.toggleFullscreen();
      } else if (e.keyCode === 77) {
        this.toggleMute();
      } else if (e.keyCode >= 48 && e.keyCode <= 57) {
        const multiplier = (e.keyCode - 48) / 10;
        this.setCurrentTime(multiplier * this.player.duration());
      } else if (e.keyCode === 37 || e.keyCode === 74) {
        const currentTime = this.player.time();
        this.setCurrentTime(Math.max(0, currentTime - 5));
      } else if (e.keyCode === 39 || e.keyCode === 76) {
        const duration = this.player.duration();
        const currentTime = this.player.time();
        this.setCurrentTime(Math.min(duration, currentTime + 5));
      } else if (e.keyCode === 36) {
        this.setCurrentTime(0);
      } else if (e.keyCode === 35) {
        this.setCurrentTime(this.player.duration());
      } else if (e.keyCode === 38) {
        let currentVolume = this.player.getVolume();
        if (this.player.isMuted()) {
          currentVolume = 0;
        }
        this.player.setVolume(Math.min(1, currentVolume + 0.05));
        this.player.setMute(false);
      } else if (e.keyCode === 40) {
        let currentVolume = this.player.getVolume();
        if (this.player.isMuted()) {
          currentVolume = 0;
        }
        this.player.setVolume(Math.max(0, currentVolume - 0.05));
        this.player.setMute(false);
      } else if (e.keyCode === 27) {
        this.goBack();
      } else {
        return false; // skip preventDefault()
      }
      e.preventDefault();
    }
  };

  onPlayClick = e => {
    const loading = !this.player || !this.player.isReady() || this.player.isSeeking();

    if (!loading && this.player.isPaused()) {
      this.player.play();
    } else {
      this.player.pause();
    }
  };

  rerender = () => {
    if (this.mounted) {
      this.forceUpdate();
    }
  };

  onProgress = throttle(
    () => {
      if (this.props.onProgress) {
        this.props.onProgress(this.player.time());
      }
    },
    this.props.progressThrottle,
    { trailing: true }
  );

  goBack = () => {
    this.player.pause();
    this.props.goBack();
  };

  setVolume = volume => {
    this.player.setVolume(volume);
  };

  togglePlayPause = () => {
    if (this.player.isPaused()) {
      this.player.play();
    } else {
      this.player.pause();
    }
  };

  toggleMute = () => {
    this.player.setMute(!this.player.isMuted());
  };

  toggleFullscreen = () => {
    if (document[fullScreenElement]) {
      document[exitFullScreen]();
    } else {
      this.videoContainer[requestFullScreen]();
    }
  };

  setCurrentTime = (currentTime, resume) => {
    this.player.seek(currentTime);

    // Need to manually pause if user holds mouse button down while seeking.
    if (resume) {
      this.player.play();
    } else {
      this.player.pause();
    }
    if (this.props.onProgress) {
      this.props.onProgress(currentTime);
    }
  };

  setContainerRef = videoContainer => {
    this.videoContainer = findDOMNode(videoContainer);
    if (this.videoContainer) {
      this.videoContainer.focus();
      this.videoContainer.addEventListener('keydown', this.onKeyDown, false);
    }
  };

  setVideoRef = videoElement => {
    this.videoElement = findDOMNode(videoElement);
  };

  renderLoading() {
    return (
      <div
        style={{
          position: 'absolute',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          background: 'rgba(0,0,0,.5)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <div className="video__loading" />
        {this.props.video.get('transcode_status') !== 'Complete' ? (
          <span className="video__uploading-message">
            This video is currently being uploaded<br />Check back in a few moments
          </span>
        ) : null}
      </div>
    );
  }

  renderPlayButton() {
    const loading = !this.player || !this.player.isReady() || this.player.isSeeking();

    if (!loading && this.player && this.player.time() < 0.1) {
      return (
        <div className="play-button">
          <Icon icon="Play" />Play
        </div>
      );
    }

    if (!loading && this.player && this.player.getVideoElement().ended) {
      return (
        <div className="play-button">
          <Icon icon="Replay" />Replay
        </div>
      );
    }

    if (!loading && this.player && this.player.isPaused()) {
      return (
        <div className="play-button">
          <Icon icon="Play" />Resume
        </div>
      );
    }
  }

  renderPlayPauseOverlay() {
    const { backText, sections } = this.props;
    const loading = !this.player || !this.player.isReady() || this.player.isSeeking();

    const backLink = backText ? (
      <button
        onClick={this.goBack}
        className={
          sections && sections.length > 0
            ? 'video-player__back-link video-player__back-link--sections'
            : 'video-player__back-link'
        }
      >
        <Icon icon="BackArrow" />
        <em>{backText}</em>
      </button>
    ) : null;

    return (
      <div
        className="video-player__overlay"
        style={{
          background: !loading && this.player.isPaused() ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0)',
        }}
        onClick={this.onPlayClick}
      >
        {this.renderPlayButton()}
        {backLink}
      </div>
    );
  }

  renderSections() {
    if (
      this.props.sections &&
      this.props.sections.length &&
      this.player &&
      this.props.sections[0].startTime < this.player.duration()
    ) {
      let prevSection;
      let currentSection;
      let nextSection;
      const currentTime = this.player.time();

      if (this.props.sections[0].startTime !== 0 && currentTime < this.props.sections[0].startTime) {
        prevSection = null;
        currentSection = { text: `Up Next: ${this.props.sections[0].text}`, startTime: 0 };
        nextSection = this.props.sections[0];
      } else {
        this.props.sections.forEach((curr, i, arr) => {
          if (currentTime >= curr.startTime) {
            prevSection = i > 0 ? arr[i - 1] : null;
            currentSection = curr;
            nextSection = i < arr.length - 1 ? arr[i + 1] : null;
          }
        });
      }

      const timeRemaining = nextSection ? nextSection.startTime - currentTime : this.player.duration() - currentTime;

      return (
        <div className="video-player__sections" style={this.player.isPaused() ? { opacity: '1' } : null}>
          <button
            onClick={
              prevSection ? this.setCurrentTime.bind(this, prevSection.startTime, !this.player.isPaused()) : null
            }
          >
            <Icon icon="Previous" className={!prevSection ? 'disabled' : ''} />
          </button>
          <span className="video-player__sections--current">{`${currentSection.text} (${numeral(timeRemaining).format(
            '00:00:00'
          )})`}</span>
          <button
            onClick={
              nextSection ? this.setCurrentTime.bind(this, nextSection.startTime, !this.player.isPaused()) : null
            }
          >
            <Icon icon="Next" className={!nextSection ? 'disabled' : ''} />
          </button>
        </div>
      );
    }
  }

  render() {
    const { className, title, poster } = this.props;
    const fullscreen = !!document[fullScreenElement];
    const loading = !this.mounted || !this.player || !this.player.isReady() || this.player.isSeeking();
    const buffered = this.mounted && this.player ? this.player.time() + this.player.getBufferLength() : 0;

    const playerClasses = classnames({
      'video-player': true,
      'video-player--fullscreen': fullscreen,
      'video-player--hover': this.state.mouseOver,
      [className]: !!className,
    });

    return (
      <div
        className={playerClasses}
        ref={this.setContainerRef}
        tabIndex={0}
        onMouseMove={this.onMouseMove}
        style={{
          userSelect: 'none',
        }}
      >
        <video poster={poster} ref={this.setVideoRef} />
        {this.renderPlayPauseOverlay()}
        {this.renderSections()}
        {this.player && (
          <VideoControls
            disabled={loading}
            buffered={buffered}
            title={title}
            currentTime={this.player.time()}
            duration={this.player.duration()}
            paused={this.player.isPaused() || false}
            muted={this.player.isMuted()}
            volume={this.player.getVolume()}
            toggleMute={this.toggleMute}
            fullscreen={fullscreen}
            toggleFullscreen={this.toggleFullscreen}
            togglePlayPause={this.togglePlayPause}
            setCurrentTime={this.setCurrentTime}
            setVolume={this.setVolume}
          />
        )}
        {loading && this.renderLoading()}
      </div>
    );
  }
}

export default DesktopVideoPlayer;



// WEBPACK FOOTER //
// ./src/common/video/DesktopVideoPlayer.js